import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const formatDetail = (detail) => {
  if (!detail) return "Accesso non riuscito. Riprova.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").filter(Boolean).join(" ");
  return String(detail);
};

const isLiveDomain = window.location.hostname.includes("nicolaverardo.it");

const PreviewBanner = () =>
  isLiveDomain ? null : (
    <div data-testid="admin-preview-banner" className="bg-gold text-night text-[11px] font-medium tracking-[0.08em] px-6 py-2.5 text-center">
      AMBIENTE DI LAVORO (anteprima) — le modifiche fatte qui NON vanno sul sito live. Per modificare il sito vero usa <strong>www.nicolaverardo.it/admin</strong>
    </div>
  );

const AdminLogin = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/admin" replace />;

  const handleSaveCredentials = () => {
    if (!email || !password) {
      toast.error("Inserisci email e password, poi memorizza.");
      return;
    }
    if (window.PasswordCredential) {
      navigator.credentials
        .store(new PasswordCredential({ id: email, password, name: email }))
        .then(() => toast.success("Credenziali memorizzate nel browser."))
        .catch(() => toast.error("Salvataggio non riuscito in questo browser."));
    } else {
      toast.error("Il tuo browser non supporta il salvataggio automatico. Usa il gestore password del browser.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      if (window.PasswordCredential) {
        navigator.credentials.store(new PasswordCredential({ id: email, password, name: email })).catch(() => {});
      }
      navigate("/admin");
    } catch (err) {
      setError(formatDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-[#26292E] flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm mb-6 self-center"><PreviewBanner /></div>
      <div className="w-full max-w-sm">
        <p className="text-sm font-semibold tracking-[0.2em] text-bone text-center">NICOLA VERARDO</p>
        <p className="mt-1 text-[10px] font-mono tracking-[0.35em] text-fog text-center">AREA RISERVATA</p>
        <form data-testid="admin-login-form" onSubmit={handleSubmit} className="mt-10 space-y-5">
          <div>
            <label htmlFor="admin-email" className="block text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-2">Email</label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-surface border border-line px-4 py-3.5 text-sm text-bone focus:border-gold focus:outline-none transition-colors duration-300"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="block text-[10px] font-mono tracking-[0.25em] text-fog uppercase mb-2">Password</label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-surface border border-line px-4 py-3.5 text-sm text-bone focus:border-gold focus:outline-none transition-colors duration-300"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-xs text-red-400">{error}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-button"
            disabled={loading}
            className="w-full px-8 py-4 bg-gold text-night text-xs font-semibold tracking-[0.2em] hover:bg-bone disabled:opacity-60 transition-colors duration-300"
          >
            {loading ? "ACCESSO…" : "ACCEDI"}
          </button>
          <button
            type="button"
            data-testid="admin-save-credentials-button"
            onClick={handleSaveCredentials}
            className="w-full px-8 py-3.5 border border-line text-fog text-[10px] font-medium tracking-[0.2em] hover:text-bone hover:border-fog transition-[color,border-color] duration-300"
          >
            MEMORIZZA CREDENZIALI NEL BROWSER
          </button>
        </form>
      </div>
    </main>
  );
};

export default AdminLogin;
